'use client';

import { ComponentPropsWithoutRef, forwardRef } from 'react';
import { useTranslations } from 'next-intl';
import { ChevronDown } from 'lucide-react';
import * as Accordion from '@radix-ui/react-accordion';
import { TextNavLink } from '@/shared/components/text-nav-link';
import { cn } from '@ecom/helper-react-class-combine';
import { useUser } from '@/features/auth/shared/hooks/use-user';
import { pagesRoutes } from '@/shared/constants/routes';
import { isGamesApp } from '@/shared/isGamesApp';

type AccordionTriggerProps = {
  className?: string;
};

const AccordionTrigger = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<'button'> & AccordionTriggerProps
>(({ children, className, ...props }, forwardedRef) => (
  <Accordion.Header className='AccordionHeader'>
    <Accordion.Trigger
      className={cn(
        'group flex w-full items-center justify-between py-4 text-lg font-semibold',
        className,
      )}
      {...props}
      ref={forwardedRef}
    >
      {children}
      <ChevronDown
        className='transition-transform duration-300 group-data-[state=open]:rotate-180'
        aria-hidden
        size={20}
      />
    </Accordion.Trigger>
  </Accordion.Header>
));

AccordionTrigger.displayName = 'AccordionTrigger';

type AccordionContentProps = {
  className?: string;
};

const AccordionContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<'div'> & AccordionContentProps
>(({ children, className, ...props }, forwardedRef) => (
  <Accordion.Content
    className={cn(
      'bg-background-subtle data-[state=closed]:animate-slide-up data-[state=open]:animate-slide-down overflow-hidden',
      className,
    )}
    {...props}
    ref={forwardedRef}
  >
    <div className='pb-2 pl-2'>{children}</div>
  </Accordion.Content>
));

AccordionContent.displayName = 'AccordionContent';

type ResponsiveNavMenuProps = {
  onLinkClick: () => void;
};

type CategoryMeta = {
  gtmId: string;
  href: string;
};

const categoryMetaMap: Record<string, CategoryMeta> = {
  'Hidden Object': {
    gtmId: 'mobile_nav_category_hidden_object',
    href: '/c/hidden-object',
  },
  Cards: {
    gtmId: 'mobile_nav_category_cards',
    href: '/c/cards',
  },
  'Match 3': {
    gtmId: 'mobile_nav_category_match_3',
    href: '/c/match-3',
  },
  Mahjong: {
    gtmId: 'mobile_nav_category_mahjong',
    href: '/c/mahjong',
  },
  'Time Management': {
    gtmId: 'mobile_nav_category_time_management',
    href: '/c/time-management',
  },
  Puzzle: {
    gtmId: 'mobile_nav_category_puzzle',
    href: '/c/puzzle',
  },
  Casino: {
    gtmId: 'mobile_nav_category_casino',
    href: '/c/casino',
  },
  Adventure: {
    gtmId: 'mobile_nav_category_adventure',
    href: '/c/adventure',
  },
};

export const ResponsiveNavMenu = ({ onLinkClick }: ResponsiveNavMenuProps) => {
  const t = useTranslations('Home.header.desktopNavMenu');
  const { authenticatedUser } = useUser();

  const isWildMember = !!authenticatedUser?.isWildMember;

  return (
    <div className='overflow-y-auto'>
      <nav className='p-2'>
        <Accordion.Root className='w-full' type='single' collapsible>
          {/* Find Games */}
          <div className='border-b-2'>
            <Accordion.Item className='AccordionItem' value='find-games'>
              <AccordionTrigger>{t('navMenuRoutes.allGames').toUpperCase()}</AccordionTrigger>
              <AccordionContent>
                <div className='bg-background-subtle space-y-2 p-4'>
                  {/* Featured Categories */}
                  {Object.entries(categoryMetaMap).map(([name, meta]) => {
                    return (
                      <TextNavLink
                        isBold
                        key={name}
                        href={meta.href}
                        prefetch
                        className='block flex items-center gap-2 py-1'
                        onClick={onLinkClick}
                        data-gtm='cta'
                        data-cta-name={meta.gtmId}
                      >
                        {name}
                      </TextNavLink>
                    );
                  })}
                  <TextNavLink
                    isBold
                    key='mobile_nav_category_more_categories'
                    href='/categories'
                    prefetch
                    className='block flex items-center gap-2 py-1'
                    onClick={onLinkClick}
                    data-gtm='cta'
                    data-cta-name='mobile_nav_category_more_categories'
                  >
                    More categories
                  </TextNavLink>
                </div>
              </AccordionContent>
            </Accordion.Item>
          </div>

          {/* Games App */}
          {isGamesApp() ? (
            <div className='border-b-2'>
              <TextNavLink
                href={pagesRoutes.library.href}
                isBold
                prefetch={false}
                className='flex items-center justify-between py-4 text-lg'
                onClick={onLinkClick}
                unlocalized
                aria-label={`${t('navigateTo')} ${t('navMenuRoutes.library')}`}
                data-gtm='cta'
                data-cta-name='mobile_nav_library'
              >
                {t('navMenuRoutes.library').toUpperCase()}
              </TextNavLink>
            </div>
          ) : (
            <div className='border-b-2'>
              <TextNavLink
                href={pagesRoutes.gamesApp.href}
                isBold
                prefetch
                className='flex items-center justify-between py-4 text-lg'
                onClick={onLinkClick}
                aria-label={`${t('navigateTo')} ${t('navMenuRoutes.gamesApp')}`}
                data-gtm='cta'
                data-cta-name='mobile_nav_games_app'
              >
                {t('navMenuRoutes.gamesApp').toUpperCase()}
              </TextNavLink>
            </div>
          )}

          {/* Join Wild (hidden for members) */}
          {!isWildMember && (
            <div className='border-b-2'>
              <TextNavLink
                href={pagesRoutes.joinWild.href}
                isBold
                prefetch
                className='flex items-center justify-between py-4 text-lg'
                onClick={onLinkClick}
                aria-label={`${t('navigateTo')} ${t('navMenuRoutes.membership')}`}
                data-gtm='cta'
                data-cta-name='mobile_nav_membership'
              >
                {t('navMenuRoutes.membership').toUpperCase()}
              </TextNavLink>
            </div>
          )}

          {/* Wild Unlimited (only for members) */}
          {isWildMember && (
            <div className='border-b-2'>
              <TextNavLink
                href={pagesRoutes.wildUnlimited.href}
                isBold
                prefetch
                className='flex items-center justify-between py-4 text-lg'
                onClick={onLinkClick}
                aria-label={`${t('navigateTo')} ${t('navMenuRoutes.wildUnlimited')}`}
                data-gtm='cta'
                data-cta-name='mobile_nav_wild_unlimited'
              >
                {t('navMenuRoutes.wildUnlimited').toUpperCase()}
              </TextNavLink>
            </div>
          )}

          {/* Support */}
          <div className='border-b-2 last:border-b-0'>
            <TextNavLink
              href={pagesRoutes.support.href}
              isBold
              className='flex items-center justify-between py-4 text-lg'
              onClick={onLinkClick}
              unlocalized
              aria-label={`${t('navigateTo')} ${t('navMenuRoutes.support')}`}
              data-gtm='cta'
              data-cta-name='mobile_nav_support'
            >
              {t('navMenuRoutes.support').toUpperCase()}
            </TextNavLink>
          </div>
        </Accordion.Root>
      </nav>
    </div>
  );
};
